// src/components/onboarding/AiSetupWizard.js
//
// The guided setup: one window that asks for the few things only the owner
// knows, and builds the rest.
//
// The problem it solves is not typing. It is that a working LoadMemo tenant is
// a shape — a company, a truck belonging to it, a driver belonging to both, an
// office with the right roles, and a load to look at — and nothing on screen
// tells a new owner that shape exists. The old guide pointed at five pages and
// left them to infer it. Half of them stopped at the first page.
//
// So every step here is short, every step saves immediately, and every step can
// be skipped. Somebody who has no truck yet should still reach the end with a
// company. What is created is identical to what the real pages create — see
// setupActions.js, which holds the writes and the reasons.

import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../firebase';
import {
  saveCommodityTypes, createCompany, createTruck,
  createDriver, createInvites, createDemoLoad,
} from './setupActions';

const COMMODITIES = [
  { id: 'automobile_hauling', label: 'Automobiles', icon: '🚗' },
  { id: 'dry_van', label: 'Dry Van', icon: '📦' },
  { id: 'reefer', label: 'Reefer', icon: '❄️' },
  { id: 'flatbed', label: 'Flatbed', icon: '🏗️' },
];

const INVITE_ROLES = [
  { role: 'Dispatcher', blurb: 'Books loads and runs drivers' },
  { role: 'Accountant', blurb: 'Invoices, statements, payments' },
  { role: 'Fleet', blurb: 'Trucks, trailers, maintenance' },
  { role: 'Admin', blurb: 'Everything except billing' },
];

const STEPS = ['Company', 'Truck', 'Driver', 'Your team', 'First load'];

// Field keys read back to the user in plain words, so the confirmation says
// "MC number" rather than "mcNumber".
const LABEL_FOR = {
  name: 'name', address: 'address', phone: 'phone',
  email: 'email', mcNumber: 'MC number', usdot: 'USDOT',
};

// Shows the shape of a useful paste without anybody having to guess at it.
const PASTE_PLACEHOLDER = [
  'Paste here, for example:',
  '',
  'Acme Transport LLC',
  '1200 Industrial Blvd, Dallas, TX 75201',
  '(214) 555-0123 · dispatch@acme.com',
  'MC-884421 · USDOT 2551903',
].join('\n');

// ───────────────────────────── small shared pieces ─────────────────────────────

const Field = ({ label, hint, children }) => (
  <label className="block mb-3">
    <span className="block text-xs font-semibold text-gray-600 mb-1">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-gray-400 mt-1">{hint}</span>}
  </label>
);

const input =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm ' +
  'focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none';

const Primary = ({ children, ...props }) => (
  <button
    {...props}
    className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 text-white
               font-bold text-sm hover:from-teal-600 hover:to-cyan-700 transition
               shadow-lg shadow-teal-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
  >
    {children}
  </button>
);

const Ghost = ({ children, ...props }) => (
  <button {...props} className="px-4 py-2.5 text-sm text-gray-500 hover:text-gray-800 transition">
    {children}
  </button>
);

// ───────────────────────────────── component ─────────────────────────────────

export default function AiSetupWizard({ user, onClose, onFinished }) {
  const tenantId = user?.tenantId;

  const [stepIndex, setStepIndex] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // What has actually been created. Each step reads the previous results so it
  // can link to them, and the finish screen reports exactly what exists.
  const [built, setBuilt] = useState({ company: null, truck: null, driver: null, invites: [], load: null });

  // Registration already asked for the company name and phone, so start from
  // those rather than making somebody type them a second time on their first
  // morning.
  const [companyForm, setCompanyForm] = useState({
    name: user?.companyName || '',
    address: '',
    phone: user?.phone || '',
    email: user?.email || '',
    mcNumber: '',
    usdot: '',
  });
  const [commodities, setCommodities] = useState(['automobile_hauling']);
  const [truckForm, setTruckForm] = useState({
    unitNumber: '', yearMake: '', type: 'Company Truck', subType: 'Financed/Owned',
    vehicleCapacity: '', axleCount: '',
  });
  const [driverForm, setDriverForm] = useState({
    name: '', phone: '', email: '', paymentType: 'percentage', paymentRate: '',
  });
  const [inviteRows, setInviteRows] = useState(
    INVITE_ROLES.map(r => ({ role: r.role, email: '' }))
  );

  // A tenant normally starts with no companies — registration does not create
  // one. But if a company does already exist, creating a second one here would
  // silently split the tenant in two: trucks on one, loads on the other, and
  // drivers invisible from half the screens. So adopt it and move on.
  // Paste-to-fill. The details already exist in an email signature or a website
  // footer; retyping them is the friction, not the knowing.
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [pasteBusy, setPasteBusy] = useState(false);
  const [pasteResult, setPasteResult] = useState(null);

  const readPastedText = async () => {
    setPasteBusy(true);
    setPasteResult(null);
    try {
      const call = httpsCallable(functions, 'extractCompanyDetails');
      const { data } = await call({ text: pasteText });

      // Only overwrite a box the model actually found something for. A blank
      // result must never wipe something already typed.
      setCompanyForm(prev => {
        const next = { ...prev };
        Object.entries(data.fields || {}).forEach(([key, value]) => {
          if (value) next[key] = value;
        });
        return next;
      });

      setPasteResult({
        found: data.found || 0,
        confidence: data.confidence,
        filled: Object.entries(data.fields || {}).filter(([, v]) => v).map(([k]) => k),
      });
    } catch (err) {
      console.error('Paste-to-fill failed:', err);
      setPasteResult({ error: err?.message || 'Could not read that. Fill the fields in below instead.' });
    } finally {
      setPasteBusy(false);
    }
  };

  const [checkingExisting, setCheckingExisting] = useState(true);
  const [adoptedExisting, setAdoptedExisting] = useState(false);

  useEffect(() => {
    if (!tenantId) { setCheckingExisting(false); return; }
    let cancelled = false;

    getDocs(query(collection(db, 'companies'), where('tenantId', '==', tenantId)))
      .then(snap => {
        if (cancelled) return;
        const existing = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .find(c => !c.parentCompanyId && c.active !== false);
        if (existing) {
          setBuilt(b => ({ ...b, company: existing }));
          setAdoptedExisting(true);
          setStepIndex(1);          // straight to the truck
        }
      })
      .catch(err => console.error('Could not check for an existing company:', err))
      .finally(() => { if (!cancelled) setCheckingExisting(false); });

    return () => { cancelled = true; };
  }, [tenantId]);

  const isAutoHauling = commodities.includes('automobile_hauling');
  const set = (setter) => (key) => (e) => setter(prev => ({ ...prev, [key]: e.target.value }));
  const setCompany = set(setCompanyForm);
  const setTruck = set(setTruckForm);
  const setDriver = set(setDriverForm);

  const advance = () => setStepIndex(i => Math.min(i + 1, STEPS.length));

  /** Run one step's writes, keep what it made, move on. Never strand the user. */
  const run = async (work) => {
    setBusy(true);
    setError(null);
    try {
      await work();
      advance();
    } catch (err) {
      console.error('Guided setup step failed:', err);
      // Deliberately not advancing: the step did not happen, and saying it did
      // would leave them with a tenant missing the piece they just filled in.
      setError(err?.message || 'Something went wrong. Nothing was saved for this step.');
    } finally {
      setBusy(false);
    }
  };

  const submitCompany = () => run(async () => {
    if (!companyForm.name.trim()) throw new Error('A company name is needed to continue.');
    const company = await createCompany({ tenantId, user, form: companyForm });
    await saveCommodityTypes({ tenantId, commodityTypes: commodities, user });
    setBuilt(b => ({ ...b, company }));
  });

  const submitTruck = () => run(async () => {
    if (!truckForm.unitNumber.trim()) throw new Error('A unit number is needed, or skip this step.');
    const truck = await createTruck({ tenantId, user, form: truckForm, company: built.company });
    setBuilt(b => ({ ...b, truck }));
  });

  const submitDriver = () => run(async () => {
    if (!driverForm.name.trim()) throw new Error('A driver name is needed, or skip this step.');
    const driver = await createDriver({
      tenantId, user, form: driverForm, company: built.company, truck: built.truck,
    });
    setBuilt(b => ({ ...b, driver }));
  });

  const submitInvites = () => run(async () => {
    const wanted = inviteRows.filter(r => r.email.trim());
    if (wanted.length === 0) return;           // skipping is a normal answer here
    const invites = await createInvites({ tenantId, user, invites: wanted, company: built.company });
    setBuilt(b => ({ ...b, invites }));
  });

  const submitDemoLoad = () => run(async () => {
    const load = await createDemoLoad({
      tenantId, user, company: built.company, driver: built.driver,
      truck: built.truck, isAutoHauling,
    });
    setBuilt(b => ({ ...b, load }));
  });

  const stepBody = () => {
    switch (stepIndex) {
      // ── 1. COMPANY ────────────────────────────────────────────────────────
      case 0:
        return (
          <>
            <p className="text-sm text-gray-500 mb-3">
              Your operating authority — the name that goes on your BOLs and invoices.
            </p>

            {/* The details already exist somewhere. Reading them beats asking
                somebody to transcribe their own address on their first morning.
                Everything lands in the editable boxes below, never straight into
                the database, so a wrong guess costs a correction. */}
            {!pasteOpen ? (
              <button
                type="button"
                onClick={() => setPasteOpen(true)}
                className="w-full mb-4 px-4 py-3 rounded-xl border-2 border-dashed border-violet-300
                           bg-violet-50 text-left hover:border-violet-400 hover:bg-violet-100 transition"
              >
                <span className="text-sm font-bold text-violet-800">
                  ✨ Paste anything and I'll fill this in
                </span>
                <span className="block text-xs text-violet-600 mt-0.5">
                  An email signature, your website footer, a letterhead, a rate confirmation
                </span>
              </button>
            ) : (
              <div className="mb-4 p-3 rounded-xl border border-violet-200 bg-violet-50">
                <textarea
                  className="w-full border border-violet-200 rounded-lg px-3 py-2 text-sm h-28 resize-none
                             focus:ring-2 focus:ring-violet-400 focus:border-violet-400 outline-none"
                  value={pasteText}
                  onChange={(e) => setPasteText(e.target.value)}
                  placeholder={PASTE_PLACEHOLDER}
                  autoFocus
                />
                <div className="flex items-center gap-2 mt-2">
                  <button
                    type="button"
                    onClick={readPastedText}
                    disabled={pasteBusy || pasteText.trim().length < 20}
                    className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-bold
                               hover:bg-violet-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {pasteBusy ? 'Reading…' : 'Read it'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setPasteOpen(false); setPasteResult(null); }}
                    className="text-xs text-gray-500 hover:text-gray-700"
                  >
                    I'll type it myself
                  </button>
                </div>

                {pasteResult?.error && (
                  <p className="text-xs text-red-700 mt-2">{pasteResult.error}</p>
                )}

                {pasteResult && !pasteResult.error && (
                  <p className={`text-xs mt-2 ${pasteResult.found ? 'text-violet-800' : 'text-amber-700'}`}>
                    {pasteResult.found === 0
                      ? "I could not find company details in that. Fill the fields in below."
                      : `Filled in ${pasteResult.filled.map(f => LABEL_FOR[f] || f).join(', ')}. Check them before continuing.`}
                    {pasteResult.confidence === 'low' && pasteResult.found > 0 &&
                      ' I am not certain this is your company — please read it over carefully.'}
                  </p>
                )}
              </div>
            )}

            <Field label="Company name">
              <input className={input} value={companyForm.name} onChange={setCompany('name')}
                     placeholder="Acme Transport LLC" autoFocus />
            </Field>
            <Field label="Address">
              <input className={input} value={companyForm.address} onChange={setCompany('address')}
                     placeholder="1200 Industrial Blvd, Dallas, TX 75201" />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone">
                <input className={input} value={companyForm.phone} onChange={setCompany('phone')}
                       placeholder="(214) 555-0123" />
              </Field>
              <Field label="Email">
                <input className={input} value={companyForm.email} onChange={setCompany('email')}
                       placeholder="dispatch@acme.com" />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="MC number" hint="Optional — you can add it later">
                <input className={input} value={companyForm.mcNumber} onChange={setCompany('mcNumber')} />
              </Field>
              <Field label="USDOT" hint="Optional">
                <input className={input} value={companyForm.usdot} onChange={setCompany('usdot')} />
              </Field>
            </div>

            {/* Asked here and nowhere else, because it changes which fields appear
                on every load screen afterwards. Getting it wrong is a far bigger
                nuisance than one extra question now. */}
            <div className="mt-2">
              <span className="block text-xs font-semibold text-gray-600 mb-2">What do you haul?</span>
              <div className="flex flex-wrap gap-2">
                {COMMODITIES.map(c => {
                  const on = commodities.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setCommodities(prev =>
                        on ? prev.filter(x => x !== c.id) : [...prev, c.id])}
                      className={`px-3 py-2 rounded-lg text-sm border transition ${
                        on ? 'bg-teal-50 border-teal-400 text-teal-800 font-semibold'
                           : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'}`}
                    >
                      {c.icon} {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        );

      // ── 2. TRUCK ──────────────────────────────────────────────────────────
      case 1:
        return (
          <>
            <p className="text-sm text-gray-500 mb-4">
              I'll link it to <span className="font-semibold text-gray-700">{built.company?.name}</span> for you.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Unit number">
                <input className={input} value={truckForm.unitNumber} onChange={setTruck('unitNumber')}
                       placeholder="101" autoFocus />
              </Field>
              <Field label="Year & make" hint="Optional">
                <input className={input} value={truckForm.yearMake} onChange={setTruck('yearMake')}
                       placeholder="2022 Freightliner" />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Ownership">
                <select className={input} value={truckForm.type} onChange={setTruck('type')}>
                  <option>Company Truck</option>
                  <option>Owner Operator</option>
                  <option>Leased</option>
                </select>
              </Field>
              <Field
                label={isAutoHauling ? 'Trailer capacity' : 'Axles'}
                hint={isAutoHauling ? 'How many cars it holds' : 'Used for toll estimates'}
              >
                <input
                  className={input}
                  type="number"
                  value={isAutoHauling ? truckForm.vehicleCapacity : truckForm.axleCount}
                  onChange={isAutoHauling ? setTruck('vehicleCapacity') : setTruck('axleCount')}
                  placeholder={isAutoHauling ? '7' : '5'}
                />
              </Field>
            </div>

            <p className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-2.5 mt-1">
              Added as <span className="font-semibold">Inactive</span>, so it does not affect
              your subscription. Switch it to Active on the Trucks page when the truck starts working.
            </p>
          </>
        );

      // ── 3. DRIVER ─────────────────────────────────────────────────────────
      case 2:
        return (
          <>
            <p className="text-sm text-gray-500 mb-4">
              I'll put them on {built.company?.name}
              {built.truck ? <> and in truck <span className="font-semibold text-gray-700">{built.truck.unitNumber}</span></> : null}.
            </p>

            <Field label="Driver name">
              <input className={input} value={driverForm.name} onChange={setDriver('name')}
                     placeholder="James Carter" autoFocus />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone">
                <input className={input} value={driverForm.phone} onChange={setDriver('phone')} />
              </Field>
              <Field label="Email" hint="They use this to sign in to the driver app">
                <input className={input} value={driverForm.email} onChange={setDriver('email')} />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Paid by">
                <select className={input} value={driverForm.paymentType} onChange={setDriver('paymentType')}>
                  <option value="percentage">% of the load</option>
                  <option value="per_mile">Per mile</option>
                </select>
              </Field>
              <Field label={driverForm.paymentType === 'percentage' ? 'Percentage' : 'Rate per mile'}>
                <input className={input} type="number" step="0.01"
                       value={driverForm.paymentRate} onChange={setDriver('paymentRate')}
                       placeholder={driverForm.paymentType === 'percentage' ? '25' : '0.55'} />
              </Field>
            </div>
          </>
        );

      // ── 4. TEAM ───────────────────────────────────────────────────────────
      case 3:
        return (
          <>
            <p className="text-sm text-gray-500 mb-4">
              Add anyone who works in the office. Leave a row blank to skip it.
            </p>

            {inviteRows.map((row, i) => {
              const meta = INVITE_ROLES.find(r => r.role === row.role);
              return (
                <div key={row.role} className="flex items-center gap-2 mb-2">
                  <div className="w-28 shrink-0">
                    <div className="text-sm font-semibold text-gray-700">{row.role}</div>
                    <div className="text-[10px] text-gray-400 leading-tight">{meta?.blurb}</div>
                  </div>
                  <input
                    className={input}
                    type="email"
                    value={row.email}
                    placeholder={`${row.role.toLowerCase()}@${(built.company?.name || 'company').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 12)}.com`}
                    onChange={(e) => setInviteRows(rows =>
                      rows.map((r, j) => j === i ? { ...r, email: e.target.value } : r))}
                  />
                </div>
              );
            })}

            <p className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-2.5 mt-3">
              LoadMemo does not email these out. I'll give you a sign-up link for each
              person on the next screen — send them however you like.
            </p>
          </>
        );

      // ── 5. DEMO LOAD ──────────────────────────────────────────────────────
      case 4:
        return (
          <>
            <p className="text-sm text-gray-500 mb-4">
              Last one. I'll create a sample load so you can see how a real one looks —
              on the board, on the map, and on an invoice.
            </p>

            <div className="border border-gray-200 rounded-xl p-4 bg-gray-50 text-sm">
              <div className="flex items-center justify-between mb-2">
                <span className="font-bold text-gray-800">DEMO-0001</span>
                <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold">
                  SAMPLE
                </span>
              </div>
              <div className="text-gray-600 space-y-1 text-xs">
                <div>Statesville, NC → Baltimore, MD</div>
                <div>$950{isAutoHauling ? ' · 2025 Jeep Compass' : ''}</div>
                {built.driver && <div>Assigned to {built.driver.name}</div>}
              </div>
            </div>

            <p className="text-[11px] text-gray-500 mt-3">
              It is tagged as a sample so it never hides in your real numbers, and you can
              delete it in one click on the next screen.
            </p>
          </>
        );

      // ── FINISHED ──────────────────────────────────────────────────────────
      default:
        return (
          <div className="text-center">
            <div className="text-5xl mb-3">🎉</div>
            <h3 className="text-2xl font-extrabold text-gray-900 mb-1">
              {built.company?.name || 'Your account'} is set up
            </h3>
            <p className="text-sm text-gray-500 mb-5">Here is what I created.</p>

            <div className="text-left bg-gray-50 rounded-xl p-4 mb-4 space-y-1.5">
              {[
                built.company && `Company — ${built.company.name}`,
                built.truck && `Truck ${built.truck.unitNumber}, linked to the company (Inactive)`,
                built.driver && `Driver ${built.driver.name}${built.truck ? `, in truck ${built.truck.unitNumber}` : ''}`,
                built.invites.length > 0 && `${built.invites.length} invite${built.invites.length === 1 ? '' : 's'} prepared`,
                built.load && 'Sample load DEMO-0001',
              ].filter(Boolean).map((line, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                  <span className="text-teal-500 font-bold">✓</span>{line}
                </div>
              ))}
            </div>

            {built.invites.length > 0 && (
              <div className="text-left mb-4">
                <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                  Send these links
                </h4>
                {built.invites.map(inv => (
                  <div key={inv.email} className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs text-gray-600 w-40 truncate">{inv.email}</span>
                    <input readOnly value={inv.link} className={`${input} text-[11px] py-1.5`}
                           onFocus={(e) => e.target.select()} />
                    <button
                      type="button"
                      onClick={() => navigator.clipboard?.writeText(inv.link)}
                      className="px-2 py-1.5 text-xs rounded-lg border border-gray-300 hover:bg-gray-50 shrink-0"
                    >
                      Copy
                    </button>
                  </div>
                ))}
                <p className="text-[11px] text-amber-700 mt-1">
                  Copy these now — they are not shown again, and nothing is emailed automatically.
                </p>
              </div>
            )}

            <Primary onClick={() => onFinished?.(built)}>Open my dashboard →</Primary>
          </div>
        );
    }
  };

  const onFinalStep = stepIndex === STEPS.length - 1;
  const isDone = stepIndex >= STEPS.length;

  const submitForStep = [submitCompany, submitTruck, submitDriver, submitInvites, submitDemoLoad][stepIndex];
  const nextLabel = ['Create my company', 'Add truck', 'Add driver', 'Send invites', 'Create sample load'][stepIndex];

  if (!tenantId) {
    return (
      <>
        <div className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm" />
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md p-6 text-center">
            <p className="text-sm text-gray-700 mb-4">
              Your account is not attached to a workspace yet, so there is nothing to set
              up here. This usually means your registration is still awaiting approval.
            </p>
            <Ghost onClick={onClose}>Back</Ghost>
          </div>
        </div>
      </>
    );
  }

  if (checkingExisting) {
    return (
      <>
        <div className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm" />
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl px-6 py-5 text-sm text-gray-500">
            Checking your workspace…
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-[9998] bg-black/50 backdrop-blur-sm" />
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl my-8 overflow-hidden">

          {!isDone && (
            <div className="px-6 pt-5">
              <div className="flex items-center gap-1.5 mb-4">
                {STEPS.map((label, i) => (
                  <div key={label} className="flex-1">
                    <div className={`h-1.5 rounded-full transition-all ${
                      i < stepIndex ? 'bg-teal-500' : i === stepIndex ? 'bg-teal-400' : 'bg-gray-200'}`} />
                    <div className={`text-[10px] mt-1 truncate ${
                      i === stepIndex ? 'text-teal-700 font-bold' : 'text-gray-400'}`}>{label}</div>
                  </div>
                ))}
              </div>
              <h2 className="text-xl font-extrabold text-gray-900">
                Step {stepIndex + 1} — {STEPS[stepIndex]}
              </h2>
            </div>
          )}

          <div className="px-6 py-5">
            {adoptedExisting && stepIndex === 1 && (
              <div className="mb-3 px-3 py-2 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
                You already have <span className="font-semibold">{built.company?.name}</span>,
                so I'll build on that instead of creating a second company.
              </div>
            )}

            {error && (
              <div className="mb-3 px-3 py-2 rounded-lg bg-red-50 border border-red-200 text-sm text-red-800">
                {error}
              </div>
            )}
            {stepBody()}
          </div>

          {!isDone && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
              {/* Skipping is a first-class answer. Somebody with no truck yet still
                  needs to reach the end with a company. */}
              <Ghost onClick={advance} disabled={busy}>
                {stepIndex === 0 ? 'Cancel' : 'Skip this'}
              </Ghost>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-xs text-gray-400 hover:text-gray-600"
                >
                  Close
                </button>
                <Primary onClick={submitForStep} disabled={busy}>
                  {busy ? 'Working…' : onFinalStep ? nextLabel : `${nextLabel} →`}
                </Primary>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
